import { DisplayObject, Graphics, Sprite, Texture } from "pixi.js"
import { Coordinate, TileSize } from "game-common/models"
import { LadderPixelRect, stackedLadderPixels } from "../stacked_ladder"
import { clamp01, easeOutCubic } from "../gui/chapter_reward_motion"
import { LandingDust, landingDustColors } from "./build_landing"

/**
 * Extending a ladder, as the builder sees it. Over the top hatch, the result shows in place: the
 * art it redraws is veiled and the new flight is ghosted exactly where the server will draw it. A
 * commit plays the growth: rungs appear up the new flight while the hatch rides up on them, and it
 * lands on its new storey with a puff of sawdust. The real art, drawn identically underneath, takes
 * over once it arrives.
 */
export const LADDER_EXTENSION_MS = {
    /** The new flight's rungs appearing, the hatch riding up on them. */
    grow: 340,
    /** The hatch's squash and rebound once it is up. */
    land: 220,
    /** How long the grown ladder waits for the server's art before giving way anyway. */
    hold: 1500,
    /** The hand-over to the real art. */
    fade: 140,
}

/** Where an extension's pieces are drawn, in the builder's map-surface pixels. */
export interface LadderExtensionLayout {
    at: Coordinate
    /** The storey whose top hatch is extended. Its section grows into a full flight and a hatch opens a storey up. */
    level: number
    /** Surface y of that storey's foot line, and of the storey above's. */
    baseline: number
    nextBaseline: number
    /** How much of the extended section stands already (a top hatch's stub): it doesn't grow again. */
    standing: number
    /** Its hatch stays, an opening in a floor, and the new one settles in above rather than the old riding up. */
    opening?: boolean
    /** A first, freestanding ladder is drawn from its own artwork; extended, it is redrawn as the flight's foot. */
    foot?: { baseline: number }
    /** The storeys around it are cut away: only the new flight is drawn, above the hatch, and nothing is veiled. */
    anchored?: boolean
}

export interface LadderVeil { x: number; y: number; from: number; to: number }

/** The storeys an extension draws in place of the real art: what it redraws, and once it grows, the new top too. */
export const ladderExtensionVeil = (layout: LadderExtensionLayout, growing: boolean): LadderVeil | undefined =>
    layout.anchored ? undefined : { x: layout.at.x, y: layout.at.y,
        from: layout.foot ? layout.level - 1 : layout.level, to: layout.level + (growing ? 1 : 0) }

/**
 * Stair art an extension draws in its own place, by cell and storey. An entry lasts while its owner
 * is on screen, so a ghost that is hidden or destroyed hands the real art back by itself.
 */
export class LadderVeils {
    private entries: (LadderVeil & { owner: DisplayObject })[] = []

    set = (owner: DisplayObject, veil?: LadderVeil) => {
        this.entries = this.entries.filter(entry => entry.owner !== owner)
        if (veil) this.entries.push({ owner, ...veil })
    }

    release = (owner: DisplayObject) => this.set(owner)

    /** The veils in force now. */
    active = (): LadderVeil[] => {
        this.entries = this.entries.filter(entry => !entry.owner.destroyed)
        return this.entries.filter(entry => entry.owner.worldVisible).map(({ x, y, from, to }) => ({ x, y, from, to }))
    }
}

export const ladderVeils = new LadderVeils()

/** What a growth is told: when the server's art is drawn, and what to raise and play as the hatch lands. */
export interface LadderGrowth { arrived: () => boolean; dust?: LandingDust; landed?: () => void }

type Phase = { kind: "ghost" } | ({ kind: "growing"; startAt: number; didLand?: boolean; fadeAt?: number } & LadderGrowth)

/** How far a new hatch drops into a floor it opens in. */
const HATCH_DROP_PX = 6

const drawPixels = (g: Graphics, pixels: LadderPixelRect[], dx: number, dy: number, clipTop = -Infinity) => {
    for (const pixel of pixels) {
        const top = Math.max(pixel.y + dy, clipTop), bottom = pixel.y + pixel.height + dy
        if (bottom > top) g.beginFill(pixel.color).drawRect(pixel.x + dx, top, pixel.width, bottom - top).endFill()
    }
}

/**
 * One extension on the builder's map surface: a ghost while it is previewed, then its growth. A
 * Sprite, so the build cursor carries it like any other hover ghost.
 */
export class LadderExtensionSprite extends Sprite {
    readonly heightBlurExempt = true
    private readonly foot = new Graphics()
    private readonly flight = new Graphics()
    private readonly hatch = new Graphics()
    private phase: Phase = { kind: "ghost" }
    private readonly flightPixels: LadderPixelRect[]
    private readonly flightHeight: number

    constructor(readonly layout: LadderExtensionLayout, private readonly options: { ghostAlpha: number; now?: () => number }) {
        super(Texture.EMPTY)
        this.flightHeight = Math.max(8, layout.baseline - layout.nextBaseline)
        this.flightPixels = stackedLadderPixels({ kind: "middle", height: this.flightHeight, ...(layout.opening ? { hatch: true } : {}) })
        if (layout.foot) drawPixels(this.foot, stackedLadderPixels({ kind: "bottom",
            height: Math.max(8, layout.foot.baseline - layout.baseline) }), 0, layout.foot.baseline)
        // Drawn about the middle of its foot line, so it squashes onto the floor it lands in.
        drawPixels(this.hatch, stackedLadderPixels({ kind: "top", height: this.flightHeight }), -TileSize / 2, 0)
        this.hatch.x = TileSize / 2
        this.position.set(layout.at.x * TileSize, 0)
        this.addChild(this.foot, this.flight, this.hatch)
        this.pose(0)
        ladderVeils.set(this, ladderExtensionVeil(layout, false))
    }

    private now = () => this.options.now?.() ?? Date.now()

    destroy(options?: Parameters<Sprite["destroy"]>[0]): void {
        ladderVeils.release(this)
        super.destroy(typeof options === "object" ? { ...options, children: true } : { children: true })
    }

    get growing(): boolean { return this.phase.kind === "growing" }

    /** Plays the growth from now, opaque, until the server's art is drawn and can take over. */
    grow = (growth: LadderGrowth) => {
        this.phase = { kind: "growing", startAt: this.now(), ...growth }
        ladderVeils.set(this, ladderExtensionVeil(this.layout, true))
        this.animate()
    }

    /** Called every frame while growing. Returns false once it has finished and is destroyed. */
    animate = (): boolean => {
        if (this.destroyed) return false
        const phase = this.phase
        if (phase.kind !== "growing") return true
        const now = this.now()
        const elapsed = now - phase.startAt
        const { grow, land, hold, fade } = LADDER_EXTENSION_MS
        if (!phase.didLand && elapsed >= grow) {
            phase.didLand = true
            this.puff(phase.dust)
            phase.landed?.()
        }
        if (phase.fadeAt === undefined && elapsed >= grow + land && (phase.arrived() || elapsed >= grow + land + hold)) {
            phase.fadeAt = now
            ladderVeils.release(this)
        }
        if (phase.fadeAt !== undefined) {
            const k = clamp01((now - phase.fadeAt) / fade)
            if (k >= 1) {
                this.destroy()
                return false
            }
            this.alpha = 1 - k
        }
        this.pose(elapsed)
        return true
    }

    private pose = (elapsed: number) => {
        const { baseline, nextBaseline, standing, opening } = this.layout
        const { grow, land } = LADDER_EXTENSION_MS
        const ghost = this.phase.kind === "ghost"
        const rise = ghost ? 1 : easeOutCubic(elapsed / grow)
        this.flight.clear()
        drawPixels(this.flight, this.flightPixels, 0, baseline, baseline - (standing + (this.flightHeight - standing) * rise))
        // With no floor to stay in, the old hatch rides the new rungs up; an opening stays and the new one settles above.
        this.hatch.y = opening ? nextBaseline - HATCH_DROP_PX * (1 - rise) : baseline - this.flightHeight * rise
        const settle = ghost ? 1 : clamp01((elapsed - grow) / land)
        const squash = settle > 0 && settle < 1 ? 0.16 * (1 - settle) ** 2 * Math.cos(settle * Math.PI * 1.5) : 0
        this.hatch.scale.set(1 + squash * 0.5, 1 - squash)
        // A ghost shows the new flight faintly, and the ladder already standing as it will look.
        const { ghostAlpha } = this.options
        this.flight.alpha = ghost ? ghostAlpha : 1
        this.hatch.alpha = ghost ? ghostAlpha : opening ? clamp01(rise * 1.6 - 0.6) : 1
    }

    private puff = (dust?: LandingDust) => {
        const { at, level, nextBaseline } = this.layout
        const seed = (at.x * 73856093) ^ (at.y * 19349663) ^ (level * 83492791)
        dust?.puff((at.x + 0.5) * TileSize, nextBaseline - 1, TileSize / 2 - 4, landingDustColors("wood"), 2,
            this.zIndex + 0.01, this.now(), seed)
    }
}
